import { useCallback } from 'react';
import { selectLocalParticipant, selectRoomMetadata } from 'state/selectors';
import { isGuestParticipant } from 'useCase/moderation/status/moderators';
import { useConnectToRoom } from 'features/PreJoin/hooks/useConnectToRoom';
import { approveWaitingUsersApi, WaitingOperation } from 'features/AccessRequests/api/accessRequestsApi';
import { getConferenceJwtToken } from 'features/AccessRequests/utils/getConferenceJwtToken';
import { optimisticallyRemoveWaitingUsers } from 'features/AccessRequests/utils/mergeJoinApprovalMetadata';
import { markWaitingListHandled } from 'features/AccessRequests/utils/waitingListRegistry';

export type WaitingRoomActionResult = { payload: true } | { error: Error };

export const useWaitingRoomActions = () => {
    const { getTokenAndWssUrl } = useConnectToRoom();

    const approveWaitingUser = useCallback(
        async (waitingListIds: string[], operation: WaitingOperation): Promise<WaitingRoomActionResult> => {
            if (waitingListIds.length === 0) {
                return { payload: true };
            }

            const localParticipant = selectLocalParticipant();
            const isLocalGuest = localParticipant != null && isGuestParticipant(localParticipant);
            const jwtToken = await getConferenceJwtToken(getTokenAndWssUrl);

            const { success } = await approveWaitingUsersApi({
                waitingListIds,
                operation,
                jwtToken,
                isLocalGuest,
            });

            if (!success) {
                return { error: new Error('approveWaitingUser failed') };
            }

            markWaitingListHandled(waitingListIds, { hideFromMetadata: true, suppressSound: true });
            optimisticallyRemoveWaitingUsers(waitingListIds, operation);

            return { payload: true };
        },
        [getTokenAndWssUrl],
    );

    const approveAllWaitingUsers = useCallback(async (): Promise<WaitingRoomActionResult> => {
        const waitingIds = selectRoomMetadata()?.joinApprovalData?.waitingUsers?.map((u) => u.waitingListId) ?? [];
        return approveWaitingUser(waitingIds, 'APPROVE');
    }, [approveWaitingUser]);

    return {
        approveWaitingUser,
        approveAllWaitingUsers,
    };
};
